// src/utils/counts.js
//
// ONE DEFINITION FOR EVERY COUNT (Foster Rd demo cleanup, WP3). Every number the AI Sentry map
// (/ai-sentry and /ai-sentry-light: top bar, EdgeNotary badge, Activity panel header and tabs,
// Briefing tiles and list, device cards) and Event Trace Light (trace header, Overview tiles)
// shows is computed here, from one row set, for one site, over one stated window.
//
//   EVENT    One security_events row: a raw policy hit (or a logged observation) as the edge
//            agent reported it. Counted per row, no deduplication.
//
//   ALERT    An event that matched a policy (it carries a policy_name) AND has severity
//            Critical, High or Warning. Alerts are a subset of events. (This was the
//            "Active Alerts" rule on /ai-sentry; /ai-sentry-light used a different one.)
//
//   FINDING  (= DETECTION; the two words name the same thing.) A correlated, deduplicated
//            pattern: ONE per source IP whose events, together, match a pattern —
//              - unauthorized access: >= 1 Critical event and a policy named "unauthorized" or
//                "critical infrastructure"; or
//              - scan pattern: >= 3 distinct targets and >= 10 events.
//            A suggested network response (type VPN_ACTION) is derived FROM a finding; it is an
//            ACTION, not a second finding, and is never counted as one.
//
//   WINDOW   The row set every count above is computed from: the selected site's most recent
//            EVENT_SAMPLE_LIMIT events (the fetch passes edge_node_id, so another site's traffic
//            cannot take a share of the limit). The label states what the rows are. When the limit
//            binds: "counts over the latest 500 events · 21:37–21:56 UTC" followed by the TOTAL part
//            below. When it does not: "all events in last 2h · …" (a time-bounded fetch) or
//            "all N events · …". A requested time range ("2h", "24h") is never presented as the
//            span of a capped row set: the span shown is the rows' own first-to-last time, and any
//            hours shown with a capped set belong to the TOTAL, stated as such. Two screens show
//            the same numbers exactly when their windows are the same; the label is how a reader
//            can tell.
//
//   TOTAL    (R-500) When the limit binds, "500" is the fetch's row limit, not a count. The true
//            number of events over a stated time bound comes from GET /topology/events/count (an
//            indexed COUNT over the site and that bound) and is shown as a separate figure:
//            label "… · total 20,725 in last 24h", event figure "500 of 20,725 events (24h)".
//            Alerts, findings, detections and device figures are NOT over the total: they stay
//            computed over the latest 500 rows (the definitions above are unchanged), and every
//            inline list that shows them beside the total prefixes them "in latest 500:".
//            While the total is loading the label says "(capped)"; if it could not be read,
//            "(capped; total unavailable)" — never a bare "500 events", never an invented total.
//
// Both event shapes are read: /api/events/ (celerway_edge_node_id, destination_ip,
// details.policy_name) and /api/topology/events (edge_node_id, dest_ip, policy_name).
// Definitions also in docs/reference/count-definitions.md.

import { isEnforcedEvent, isIsolatingEvent } from './monitorMode';

export const EVENT_SAMPLE_LIMIT = 500;
// The AI Sentry pages' row fetch has no time bound, so their true total is stated over this window.
export const EVENT_TOTAL_HOURS = 24;
export const ALERT_SEVERITIES = ['critical', 'high', 'warning'];
export const FINDING_TYPES = ['ISOLATION', 'RECOMMENDATION'];
export const ACTION_TYPE = 'VPN_ACTION';

const lower = (v) => String(v ?? '').trim().toLowerCase();

export const eventSiteId = (e) => e?.celerway_edge_node_id ?? e?.edge_node_id ?? null;
export const eventDestIp = (e) => e?.destination_ip ?? e?.dest_ip ?? null;
export const eventPolicyName = (e) => e?.policy_name || e?.details?.policy_name || '';
export const isCriticalEvent = (e) => lower(e?.severity) === 'critical';
export const isAlert = (e) => !!eventPolicyName(e) && ALERT_SEVERITIES.includes(lower(e?.severity));
export const isFinding = (t) => FINDING_TYPES.includes(t?.type);
export const isAction = (t) => t?.type === ACTION_TYPE;

export const siteEvents = (events, siteId) =>
  (events || []).filter((e) => !siteId || eventSiteId(e) === siteId);

export const siteThreats = (threats, siteId) =>
  (threats || []).filter((t) => !siteId || t.agentId === siteId);

const realIp = (ip) => ip && ip !== 'N/A';

// Correlate events into findings (one per source IP) plus the suggested action for each
// unauthorized-access finding. Moved here from AISentry.jsx / AISentryLight.jsx, which carried
// two copies; both pages re-export it as detectThreatsHeuristic.
export const detectFindings = (events) => {
  const threats = [];
  const bySource = {};

  (events || []).forEach((event) => {
    const sourceIp = event.source_ip;
    const destIp = eventDestIp(event);
    const policyName = eventPolicyName(event);
    if (!realIp(sourceIp) || !realIp(destIp)) return;

    if (!bySource[sourceIp]) {
      bySource[sourceIp] = { targets: new Set(), criticalCount: 0, policies: new Set(), events: [], agentId: null };
    }
    const s = bySource[sourceIp];
    s.targets.add(destIp);
    s.events.push(event);
    if (policyName) s.policies.add(policyName);
    if (isCriticalEvent(event)) s.criticalCount++;
    if (eventSiteId(event)) s.agentId = eventSiteId(event);
  });

  Object.entries(bySource).forEach(([sourceIp, data]) => {
    const policies = Array.from(data.policies);
    const hasUnauthorized = policies.some((p) =>
      p.toLowerCase().includes('unauthorized') || p.toLowerCase().includes('critical infrastructure'));

    if (data.criticalCount > 0 && hasUnauthorized) {
      // Monitor mode: say "blocked"/"isolated" only if an event's real action (rule_action) did that.
      const enforced = data.events.some(isEnforcedEvent);
      const isolated = data.events.some(isIsolatingEvent);
      threats.push({
        id: `isolation-${sourceIp}`,
        type: 'ISOLATION',
        device: sourceIp,
        targetDevices: Array.from(data.targets),
        title: isolated ? 'Host isolated' : enforced ? 'Access blocked' : 'Unauthorized access',
        reason: isolated ? 'Unauthorized access — host isolated'
          : enforced ? 'Unauthorized access blocked' : 'Unauthorized access detected',
        enforced,
        isolated,
        confidence: Math.min(98, 85 + data.criticalCount * 3),
        severity: 'critical',
        agentId: data.agentId,
      });
      // The suggested network response for that finding (an action, not a finding).
      if (data.agentId) {
        threats.push({
          id: `response-${sourceIp}`,
          type: ACTION_TYPE,
          device: sourceIp,
          targetDevices: Array.from(data.targets),
          reason: 'Recommend network isolation response',
          confidence: Math.min(96, 74 + data.criticalCount * 4 + Math.min(data.targets.size, 3) * 2),
          severity: 'critical',
          agentId: data.agentId,
          actionDetails: {
            actionType: 'Tag Assignment',
            description: 'Apply security tag to router via Nimbus API',
            tagPrefix: 'aisentry-isolated',
            visibleIn: 'Nimbus Portal',
          },
        });
      }
    } else if (data.targets.size >= 3 && data.events.length >= 10) {
      threats.push({
        id: `scan-${sourceIp}`,
        type: 'RECOMMENDATION',
        device: sourceIp,
        targetDevices: Array.from(data.targets),
        reason: 'Port scan pattern detected',
        confidence: Math.min(90, 70 + data.targets.size * 4),
        severity: 'high',
        agentId: data.agentId,
      });
    }
  });

  return threats.sort((a, b) => {
    const sevOrder = { critical: 0, high: 1, medium: 2 };
    return (sevOrder[a.severity] || 3) - (sevOrder[b.severity] || 3);
  });
};

// Event timestamps arrive naive (UTC, no zone) from /api/events/; treat them as UTC.
export const parseEventTime = (ts) => {
  if (!ts) return null;
  const s = String(ts);
  const zoned = /[zZ]$|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`;
  const d = new Date(zoned);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtCount = (n) => Number(n).toLocaleString('en-US');

// The true event total for the fetched window (GET /topology/events/count), as the screens read it:
//   { status: 'ok', count, hoursBack, siteId }   the server's count
//   { status: 'unavailable', hoursBack, siteId } the request failed or answered something that is not a count
//   null / undefined                             not requested yet (pending)
// Never throws: a failure is a state the label shows, not a silent zero.
export const fetchEventTotal = async (getTotal, { siteId = null, hoursBack }) => {
  try {
    const r = await getTotal({ hours_back: hoursBack, ...(siteId ? { edge_node_id: siteId } : {}) });
    const body = r && r.data !== undefined && r.count === undefined ? r.data : r;
    const count = body?.count;
    if (!Number.isInteger(count) || count < 0) throw new Error(`not a count: ${JSON.stringify(body)}`);
    return { status: 'ok', count, hoursBack, siteId };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Event total unavailable; the screens will say the event figure is capped:', err?.message || err);
    return { status: 'unavailable', hoursBack, siteId };
  }
};

const pad = (n) => String(n).padStart(2, '0');
const hhmm = (d) => `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
const mmdd = (d) => `${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

export const formatHours = (h) => (h < 1 ? `${Math.round(h * 60)}m` : `${h}h`);

// The window a row set actually covers, and its label. `total` (see fetchEventTotal) is only read
// when the limit binds.
export const eventWindow = (events, { limit = EVENT_SAMPLE_LIMIT, hoursBack = null, total = null } = {}) => {
  const times = (events || []).map((e) => parseEventTime(e.timestamp)).filter(Boolean);
  const count = (events || []).length;
  const capped = !!limit && count >= limit;
  if (times.length === 0) {
    return { start: null, end: null, count, capped, limit, hoursBack,
      label: hoursBack ? `no events in last ${formatHours(hoursBack)}` : 'no events', eventsText: `${count} events`, rowsPrefix: '' };
  }
  const start = new Date(Math.min(...times));
  const end = new Date(Math.max(...times));
  const span = mmdd(start) === mmdd(end)
    ? `${hhmm(start)}–${hhmm(end)} UTC`
    : `${mmdd(start)} ${hhmm(start)}–${mmdd(end)} ${hhmm(end)} UTC`;
  let label;
  let eventsText = `${count} events`;
  // Prefix for an inline list of figures computed over the rows (alerts, findings), so that beside
  // a true total they name their row set: "in latest 500: 384 alerts".
  let rowsPrefix = '';
  if (capped) {
    const rowsPart = `counts over the latest ${limit} events`;
    rowsPrefix = `in latest ${limit}: `;
    const known = total?.status === 'ok';
    if (known) {
      const H = formatHours(total.hoursBack);
      const totalPart = `total ${fmtCount(total.count)} in last ${H}`;
      if (total.count >= count) {
        label = `${rowsPart} · ${span} · ${totalPart}`;
        eventsText = `${count} of ${fmtCount(total.count)} events (${H})`;
      } else {
        // The latest rows reach back past the total's window (a quiet site): no "of" relation holds.
        label = `${rowsPart} (capped) · ${span} · ${totalPart}`;
        eventsText = `${count} events (capped)`;
      }
    } else {
      const why = total?.status === 'unavailable' ? 'capped; total unavailable' : 'capped';
      label = `${rowsPart} (${why}) · ${span}`;
      eventsText = `${count} events (capped)`;
    }
  } else if (hoursBack) label = `all events in last ${formatHours(hoursBack)} · ${span}`;
  else label = `all ${count} events · ${span}`;
  return { start, end, count, capped, limit, hoursBack, label, eventsText, rowsPrefix };
};

// Every count for one site over one row set. `threats` may be passed when the caller already
// correlated the same rows; otherwise findings are correlated here from the site's events.
// `total` is the site's true event total (fetchEventTotal). One fetched for a different named site is
// ignored (a site switch in flight); with no siteId the rows are the caller's already-scoped fetch.
export const computeCounts = ({ events, threats, siteId = null, limit = EVENT_SAMPLE_LIMIT, hoursBack = null, total = null } = {}) => {
  const rows = siteEvents(events, siteId);
  const siteTotal = total && (!siteId || !total.siteId || total.siteId === siteId) ? total : null;
  const window = eventWindow(rows, { limit, hoursBack, total: siteTotal });
  const all = siteThreats(threats || detectFindings(rows), siteId);
  const findings = all.filter(isFinding);
  return {
    siteId,
    events: rows.length,
    alerts: rows.filter(isAlert).length,
    critical: rows.filter(isCriticalEvent).length,
    findings: findings.length,
    actions: all.filter(isAction).length,
    findingList: findings,
    window,
    // The event figure to display: "500 of 20,725 events (24h)" / "500 events (capped)" / "37 events".
    eventsText: window.eventsText,
    // "in latest 500: " when capped (prefix for alerts/findings shown beside the total), else "".
    rowsPrefix: window.rowsPrefix,
  };
};
